export const STORE_CHECKOUT_STEP_LABELS = {
  PICK_STORE_TYPE: 'Pick Store Type',
  ADD_STORE_INFO: 'Add Store Info',
  CONFIRM_SIZES: 'Confirm Sizes',
  REVIEW_CONFIRM_ORDER_ITEMS: 'Review and Confirm Order Items',
} as const;

export const STORES_FORM_MIXPANEL_SECTIONS = {
  STORE_TYPE: 'Store Type',
  STORE_INFO: 'Store Info',
  SIZES: 'Sizes',
  ORDER_DETAILS: 'Order Details',
  SHIPPING_INFO: 'Shipping Info',
} as const;

export const STORE_CHECKOUT_LOADING_TIP = 'Loading checkout...';

export const STORE_CHECKOUT_LAUNCHING_TIP = 'Launching Store';

export const STORE_CHECKOUT_PLACING_ORDER_TIP = 'Placing Your Order...';

export const STORE_CHECKOUT_PICK_STORE_TYPE_OPTIONS = [
  {
    value: 'group-order',
    title: 'Group Order',
    description: 'Let members place their own orders through a shared online store.',
    icon: '/src/assets/svg/group_order.svg',
    className: 'group-order',
  },
  {
    value: 'fundraiser',
    title: 'Fundraiser',
    description: 'Raise money by adding a donation amount to every purchase.',
    icon: '/src/assets/svg/fundraiser.svg',
    className: 'fundraiser',
  },
  {
    value: 'brand-drop',
    title: 'Brand Drop',
    description: 'Set your own commission and earn from every purchase made in your store.',
    icon: '/src/assets/svg/brand_drop.svg',
    className: 'brand-drop',
  },
  {
    value: 'permanent-store',
    title: 'Permanent Store',
    description: 'No deadlines. Your store stays open forever and can be edited anytime.',
    icon: '/src/assets/svg/permanent_store.svg',
    className: 'permanent',
  },
] as const;

export const getStoreCheckoutPickStoreTypeTitle = (storeType: string): string => {
  const option = STORE_CHECKOUT_PICK_STORE_TYPE_OPTIONS.find(
    (pickStoreTypeOption) => pickStoreTypeOption.value === storeType
  );

  return option?.title ?? storeType;
};

export const STORE_CHECKOUT_DATE_PICKER_POPUP_CLASS = 'store-checkout-date-picker-dropdown';

export const STORE_SHIPPING_TO_LABEL = 'Shipping To';

export const STORE_CHECKOUT_DELIVERY_SHIP_TYPE_PILL_STYLE = {
  STANDARD: {
    border: '#BDE8DC',
    color: '#33A687',
    backgroundDefault: '#EAF8F4',
    backgroundSelected: '#EAF8F4',
  },
  FREE: {
    border: 'transparent',
    color: '#474B6D',
    background: '#F0EDFF',
  },
} as const;

export const STORE_RECENT_ADDRESS_MAX_COUNT = 3;

export const STORE_RECENT_ADDRESS_TITLE_WITH_OPTIONS = 'Pick or Add a Shipping Address';

export const STORE_RECENT_ADDRESS_TITLE_WITHOUT_OPTIONS = 'Add Shipping Address';

export const STORE_ORDER_SUMMARY_EST_DELIVERY_BY_LABEL = 'Est. Delivery By';

export const STORE_ORDER_SUMMARY_EST_DELIVERY_MIN_DAYS_AFTER_CLOSE = 15;

export const STORE_ORDER_SUMMARY_EST_DELIVERY_MAX_DAYS_AFTER_CLOSE = 20;

export const getStoreOrderSummaryEstDeliveryTooltip = (storeCloseDate: string): string =>
  `This date is only applicable if store tips on ${storeCloseDate}.`;

export const STORE_ORDER_STEP_CONFIG = {
  STORE_TYPE: {
    title: 'Pick Store Type',
    className: 'store-type',
  },
  SHIPPING: {
    title: 'Add Shipping Info',
    className: 'shipping-details',
  },
} as const;

export const STORE_ORDER_SUMMARY_TEXT = 'Confirm & Launch Store';
export const STORE_ORDER_SUMMARY_TEXT_MOBILE = 'Confirm & Launch Store';
export const STORE_CHECKOUT_FOOTER_SUMMARY_TEXT = 'Order Summary';
export const STORE_CHECKOUT_FOOTER_SUMMARY_TEXT_MOBILE = 'Order Summary';
export const STORE_CHECKOUT_MOBILE_FOOTER_DESCRIPTION =
  'Customize your branding, pricing, and shipping.';

export const STORE_CHECKOUT_MOBILE_FIXED_HEADER_HEIGHT = '72px';
export const STORE_CHECKOUT_MOBILE_SUMMARY_OVERLAY_HEIGHT = '100dvh';
export const STORE_CHECKOUT_MOBILE_SUMMARY_MODAL_MAX_HEIGHT = '90dvh';
export const STORE_CHECKOUT_MOBILE_SUMMARY_OVERLAY_Z_INDEX = 1002;
export const STORE_CHECKOUT_MOBILE_SUMMARY_MODAL_Z_INDEX = 1003;

export const STORE_BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
  PRICING_MOBILE: 1350,
} as const;

export const STORE_CHECKOUT_LAYOUT = {
  CONTAINER_PADDING_X_PX: 40,
  ORDER_SUMMARY_WIDTH_PX: 380,
  STEPPER_PADDING_LEFT_PX: 46,
  STEP_CONTENT_PADDING_X_PX: 24,
  STEP_CONTENT_PADDING_BOTTOM_PX: 24,
  SUMMARY_MARGIN_Y_PX: 40,
} as const;

export const STORE_FORM_DEFAULTS = {
  TYPE: 'groupOrder',
  SHIPPING_OPTION: 'onePlace',
  PRICING_STRATEGY: 'drops',
} as const;

export const STORE_ORDER_TYPES = {
  GROUP_ORDER: 'group_order',
  BRAND_DROP: 'brand_drop',
  FUNDRAISER: 'fundraiser',
  PERMANENT: 'permanent',
} as const;

export const STORE_ORDER_TYPE_OPTIONS = [
  {
    value: STORE_ORDER_TYPES.GROUP_ORDER,
    title: 'Group Order',
    description: 'Lets your client collect sizes & payment for their organization.',
    icon: '/src/assets/svg/group_order_black.svg',
    selectedIcon: '/src/assets/svg/group_order_blue.svg',
    className: 'group-order',
  },
  {
    value: STORE_ORDER_TYPES.FUNDRAISER,
    title: 'Fundraiser',
    description: "Lets you add a donation $ amount per item that goes to your client's cause.",
    icon: '/src/assets/svg/fundraiser.svg',
    selectedIcon: '/src/assets/svg/fundraiser_blue.svg',
    className: 'fundraiser',
  },
  {
    value: STORE_ORDER_TYPES.BRAND_DROP,
    title: 'Brand Drop',
    description: 'Lets you add a markup per item that goes to your client.',
    icon: '/src/assets/svg/brand_drop.svg',
    selectedIcon: '/src/assets/svg/brand_drop-blue.svg',
    className: 'brand-drop',
  },
] as const;

export const STORE_INFO_CHAR_LIMITS = {
  ORDER_NAME: 50,
  STORE_DESCRIPTION: 300,
  CHARITY_NAME: 30,
} as const;

export const STORE_LOGO_UPLOAD = {
  VALID_MIME_TYPES: ['image/jpeg', 'image/jpg', 'image/png'] as const,
  ACCEPT_ATTR: 'image/jpeg,image/jpg,image/png',
  PREVIEW_WIDTH_PX: 128,
  PREVIEW_HEIGHT_DEFAULT_PX: 128,
  PREVIEW_HEIGHT_WITH_LOGO_PX: 72,
  UPLOAD_BORDER_RADIUS_PX: 12,
  UPLOAD_PADDING_PX: 6,
  S3_FOLDER: 'designs',
} as const;

export const STORE_PRIMARY_COLOR_SOURCE = {
  PRESET: 'preset',
  CUSTOM: 'custom',
  EXTRACTED: 'extracted',
} as const;

export const STORE_LOGO_SOURCE = {
  DEFAULT: 'default',
  UPLOADED: 'uploaded',
} as const;

export const STORE_LOGO_UPLOAD_MESSAGES = {
  UPLOADING_LABEL: 'uploading',
  INVALID_FILE_TYPE: 'Please upload only JPG or PNG images',
  INVALID_DIMENSIONS: 'Oops! Invalid dimensions. Please upload horizontal logo.',
  UNABLE_TO_LOAD: 'Unable to load image',
  HORIZONTAL_LOGO_HELPER: '*Upload horizontal logos only',
} as const;

export const STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES: readonly string[] = [
  '#33A687',
  '#4A90E2',
  '#FF6B6B',
  '#FFD93D',
  '#9B59B6',
  '#3498DB',
  '#E74C3C',
  '#F39C12',
];

export const STORE_SHIPPING_OPTION_TYPES = {
  BULK_SHIPPING: 'bulk_shipping',
  INDIVIDUAL_SHIPPING: 'individual_shipping',
} as const;

/** Matches the `order.shipping_type` values the API stores and returns. */
export type StoreShippingOptionType =
  (typeof STORE_SHIPPING_OPTION_TYPES)[keyof typeof STORE_SHIPPING_OPTION_TYPES];

export const STORE_SHIPPING_DELIVERY_OPTION_TYPES = {
  STANDARD: 'standard',
  MTO: 'mto',
  INDIVIDUAL: 'individually_shipped',
} as const;

export const STORE_CHECKOUT_DELIVERY_SHIP_TYPE_OPTIONS = [
  {
    label: 'Standard',
    shipTypeKey: STORE_SHIPPING_DELIVERY_OPTION_TYPES.STANDARD,
  },
  {
    label: 'Free',
    shipTypeKey: STORE_SHIPPING_DELIVERY_OPTION_TYPES.MTO,
  },
] as const;

export const getStoreCheckoutDeliveryShipTypePillStyles = (
  shipTypeKey: string,
  isSelected: boolean
): { background: string; border: string; color: string } => {
  const isStandard = shipTypeKey === STORE_SHIPPING_DELIVERY_OPTION_TYPES.STANDARD;

  if (isStandard) {
    const { border, color, backgroundDefault, backgroundSelected } =
      STORE_CHECKOUT_DELIVERY_SHIP_TYPE_PILL_STYLE.STANDARD;

    return {
      border: `1px solid ${border}`,
      background: isSelected ? backgroundSelected : backgroundDefault,
      color,
    };
  }

  const { border, color, background } = STORE_CHECKOUT_DELIVERY_SHIP_TYPE_PILL_STYLE.FREE;

  return {
    border: `1px solid ${border}`,
    background,
    color,
  };
};

export const STORE_SHIPPING_OPTIONS = [
  {
    value: STORE_SHIPPING_OPTION_TYPES.BULK_SHIPPING,
    title: 'Ship Everything to One Place',
    description: 'All ordered items will be delivered to one address.',
    className: 'bulk-shipping',
  },
  {
    value: STORE_SHIPPING_OPTION_TYPES.INDIVIDUAL_SHIPPING,
    title: 'Ship Everything Directly to Whoever Orders',
    description: 'Customers will select and pay for shipping when they checkout.',
    className: 'individual-shipping',
  },
] as const;

/** Default shipping card selection on new store checkout (Ship to One Place). */
export const STORE_DEFAULT_SHIPPING_FORM_OPTION = 'one-place' as const;

export const STORE_SHIPPING_MOQ_PRICING_REVIEW_WARNING_PREFIX =
  'Shipping updated. Please review your updated pricing and MOQs.';
export const STORE_SHIPPING_MOQ_PRICING_REVIEW_WARNING_ACTION_LABEL = 'Review now';

export const STORE_SHIPPING_FORM_LABELS = {
  SHIPPING_NAME: 'Shipping Name',
  ATTENTION_NAME: 'Attention Name (Optional)',
  STREET2: 'Apartment/Suite # (Optional)',
  STREET1: 'Street # or Name',
  CITY: 'City',
  STATE: 'State',
  ZIP_CODE: 'Zip Code',
} as const;

export const STORE_STATE_SELECT_NOT_FOUND_MESSAGE = 'No states found';
export const STORE_STATE_SELECT_SEARCH_PLACEHOLDER = 'e.g. NY';

export const STORE_SHIPPING_ADDRESS_ERROR_MESSAGES = {
  SHIPPINGNAME_REQUIRED: 'Shipping name is required',
  SHIPPINGNAME_MAX: "Shipping name can't exceed 35 characters",
  ATTENTIONNAME_MAX: "Attention name can't exceed 35 characters",
  STREET1_REQUIRED: 'Street address is required',
  CITY_REQUIRED: 'City is required',
  STATE_REQUIRED: 'State is required',
  ZIP_REQUIRED: 'Zip code is required',
  ZIP_INVALID: 'Please enter a valid zip code',
} as const;

export const STORE_EDIT_SAVE_SUCCESS_MESSAGE = 'Your changes have been saved.';

export const STORE_EDIT_BANNER_MAIL_ICON_SRC = '/src/assets/svg/accounts/mail.svg';

export const STORE_EDIT_SUPPORT_EMAIL = 'sales@freshprints.com';

export const STORE_EDIT_STORE_TYPE_LOCKED_BANNER =
  '🔒 You cannot change store type in a live store. This store has already started receiving orders. Please create a new store or contact support.';

/** Wait for async form/cart hydration before capturing the edit-page dirty baseline. */
export const STORE_EDIT_BASELINE_STABILIZATION_MS = 400;

export const STORE_EDIT_LEAVE_MODAL_Z_INDEX = 10050;

export const STORE_NEW_SETUP_LEAVE_MODAL_PRIMARY_TEXT =
  'Hold on! Going back will delete your progress.';
export const STORE_NEW_SETUP_LEAVE_MODAL_DISCARD_LABEL = 'Go Back';
export const STORE_NEW_SETUP_LEAVE_MODAL_CONTINUE_LABEL = 'Continue Setup';

export const STORE_LAUNCH_DISABLED_MESSAGES = {
  NO_CHANGES_TO_SAVE: 'Make changes to save your store.',
  STORE_TYPE_REQUIRED: 'Store type is mandatory to create store.',
  STORE_NAME_REQUIRED: 'Store name is mandatory to create store.',
  ORDER_UNTIL_REQUIRED: 'Order closing date is mandatory to create store.',
  PRODUCT_TITLE_REQUIRED: 'Product title is mandatory to create store.',
  STORE_DESCRIPTION_REQUIRED: 'Store description is mandatory to create store.',
  CHARITY_NAME_REQUIRED: 'Charity name is mandatory to create store.',
  DONATION_AMOUNT_REQUIRED: 'Please add a donation amount to at least one item.',
  MARKUP_AMOUNT_REQUIRED: 'Please add a markup amount to at least one item.',
  REVIEW_ITEMS_REQUIRED: 'Please review and confirm order items to proceed with the store.',
  PRICING_OPTION_REQUIRED: 'Please select a pricing option for each item to proceed with a store.',
  SIZE_SELECTION_REQUIRED: 'Please select a size for each item to proceed with the store.',
  SHIPPING_ADDRESS_REQUIRED: 'Shipping address is mandatory to create store.',
  PAYMENT_OPTION_REQUIRED: 'Please select a payment option to proceed with the store.',
  SHIPPING_TYPE_REQUIRED: 'Please select a shipping type to proceed with the store.',
  SHIPPING_NAME_REQUIRED: 'Who should we ship this to?',
  SHIPPING_STREET_REQUIRED: 'Street address is mandatory to create store.',
  SHIPPING_CITY_REQUIRED: 'City is mandatory to create store.',
  SHIPPING_STATE_REQUIRED: 'State is mandatory to create store.',
  SHIPPING_ZIP_REQUIRED: 'Zip code is mandatory to create store.',
  SHIPPING_ZIP_INVALID: 'Please enter a valid zip code.',
  BLOCKED_SELF_CHECKOUT: 'Item(s) in your order have an issue. Please review them.',
} as const;

export const STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE = {
  fontFamily: "'Nunito Sans', sans-serif",
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: '#474B6D',
  padding: '4px 8px',
  background: '#fff',
  boxShadow: '0px 4px 15px #8588AC26',
  borderRadius: '2px',
  maxWidth: '250px',
} as const;

export const STORE_CHECKOUT_BRANDING = {
  OPTION_DEFAULT: 'default',
  OPTION_CUSTOM: 'custom',
  DEFAULT_FP_LOGO_SRC: '/src/assets/png/fp.png',
  DEFAULT_FP_BRAND_COLOR: '#33A687',
  CUSTOM_BRANDING_DEFAULT_SWATCH: '#33A687',
} as const;

export const STORE_CHECKOUT_UI_VARIATION_VALUES = {
  DEFAULT: 'default',
  DEFAULT_FRESH_PRINT: 'default-fresh-print',
  ERROR_STATE: 'error-state',
  PHASE_2: 'phase-2',
  SINGLE_IMAGE_GENERATION: 'single-image-generation',
} as const;

export type StoreCheckoutUiVariation =
  (typeof STORE_CHECKOUT_UI_VARIATION_VALUES)[keyof typeof STORE_CHECKOUT_UI_VARIATION_VALUES];

export const isPhase1StyleStoreCheckoutUiVariation = (
  variation: StoreCheckoutUiVariation
): boolean =>
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.PHASE_2 ||
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.DEFAULT_FRESH_PRINT ||
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.SINGLE_IMAGE_GENERATION ||
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.ERROR_STATE;

export const showsFundraiserStoreInfoFieldsInCheckoutVariation = (
  variation: StoreCheckoutUiVariation
): boolean =>
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.DEFAULT ||
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.PHASE_2;

export const isFreshPrintLogoStoreCheckoutUiVariation = (
  variation: StoreCheckoutUiVariation
): boolean => variation === STORE_CHECKOUT_UI_VARIATION_VALUES.DEFAULT_FRESH_PRINT;

export const isSingleImageGenerationStoreCheckoutUiVariation = (
  variation: StoreCheckoutUiVariation
): boolean => variation === STORE_CHECKOUT_UI_VARIATION_VALUES.SINGLE_IMAGE_GENERATION;

export const isErrorStateStoreCheckoutUiVariation = (
  variation: StoreCheckoutUiVariation
): boolean => variation === STORE_CHECKOUT_UI_VARIATION_VALUES.ERROR_STATE;

export const showsStoreCheckoutAiImageGenerationActions = (
  variation: StoreCheckoutUiVariation
): boolean =>
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.DEFAULT ||
  isSingleImageGenerationStoreCheckoutUiVariation(variation) ||
  isErrorStateStoreCheckoutUiVariation(variation);

export const showsStoreCheckoutPaymentOptionStep = (variation: StoreCheckoutUiVariation): boolean =>
  variation === STORE_CHECKOUT_UI_VARIATION_VALUES.DEFAULT;

export const STORE_CHECKOUT_UI_VARIATION_OPTIONS = [
  { label: 'Phase 2', value: STORE_CHECKOUT_UI_VARIATION_VALUES.PHASE_2 },
  { label: 'final flow', value: STORE_CHECKOUT_UI_VARIATION_VALUES.DEFAULT },
  {
    label: 'Single image generation',
    value: STORE_CHECKOUT_UI_VARIATION_VALUES.SINGLE_IMAGE_GENERATION,
  },
  { label: 'Error State', value: STORE_CHECKOUT_UI_VARIATION_VALUES.ERROR_STATE },
] as const;

export const shouldShowStoreCheckoutUiVariationPicker = (): boolean =>
  !window.location.pathname.includes('/store/edit/');

export const getStoreCheckoutUiVariationModifierClass = (
  baseClass: string,
  variation: StoreCheckoutUiVariation
): string => `${baseClass}--ui-${variation}`;

export const STORE_REVIEW_PRODUCT_TITLE_LABEL = 'Product Title';
export const STORE_REVIEW_PRODUCT_INFO_LABEL = 'Product Info';
export const STORE_REVIEW_PRODUCT_INFO_EMPTY_MESSAGE = 'No product details available.';
export const STORE_REVIEW_FINAL_PRICE_LABEL = 'Final Price';
export const STORE_REVIEW_DONATION_PER_ITEM_LABEL = 'Donation Amount';
export const STORE_REVIEW_MARKUP_PER_ITEM_LABEL = 'Markup Amount';
export const STORE_REVIEW_DONATION_INPUT_UNIT_SUFFIX = '/item';

export const STORE_DONATION_MARKUP_AMOUNT_LIMITS = {
  MIN: 1,
  MAX: 99999,
} as const;

export const getStoreReviewPerItemAdjustmentLabel = (
  storeType: 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store' | undefined
): string =>
  storeType === 'brand-drop'
    ? STORE_REVIEW_MARKUP_PER_ITEM_LABEL
    : STORE_REVIEW_DONATION_PER_ITEM_LABEL;

export const FUNDRAISER_DONATION_PURCHASE_MESSAGES = {
  WITH_CHARITY: 'With every purchase, {amount} will be donated to {charityName}.',
  WITHOUT_CHARITY: 'With every purchase, {amount} goes toward a good cause.',
} as const;
export const STORE_REVIEW_PRICE_PER_ITEM_UNIT_SUFFIX = '/Item';

export const STORE_BULK_PRICING_TYPE = {
  PRICE_DROPS: 'price_drops',
  PRICE_STAYS_SAME: 'price_stays_same',
} as const;

/** Static previous unit price for the first checkout item (until visit pricing API is available). */
export const STORE_REVIEW_FIRST_ITEM_STATIC_PREVIOUS_UNIT_PRICE = 35;
export const STORE_REVIEW_PRODUCT_INFO_BULLETS = [
  '50% cotton',
  '50% polyester',
  'Best lightweight material',
] as const;

export const getStoreDesignMinimumSubtitle = (minimumUnits: number): string =>
  `Your order will only be printed if this design reaches ${minimumUnits} total units. All orders with this design are added together to reach the minimum.`;

export const STORE_MESSAGES = {
  EMPTY_CART: 'Add items to your store cart before checkout.',
  BOOTSTRAP_FAILED: 'Failed to start store checkout. Please try again.',
  LAUNCH_SUCCESS: 'Store launched successfully!',
  LAUNCH_FAILED: 'Failed to launch store. Please try again.',
  DESIGN_GROUP_REMOVED: 'Design group removed successfully.',
  DESIGN_GROUP_REMOVE_FAILED: 'Failed to remove design group.',
  ORDER_ITEM_REMOVED: 'Order item removed successfully.',
  ORDER_ITEM_REMOVE_FAILED: 'Failed to remove order item.',
  STORE_DELETE_FAILED: 'Failed to delete store. Please try again.',
} as const;

export const STORE_NAVIGATION = {
  STORE_CART: '/store-cart',
  UNAUTHORIZED: '/portal',
} as const;

export const STORE_CONFIRM_SIZES_SIZES_OPTIONS = [
  { label: 'OS', value: 'OS' },
  { label: '2XS', value: '2XS' },
  { label: 'XS', value: 'XS' },
  { label: 'S', value: 'S' },
  { label: 'M', value: 'M' },
  { label: 'L', value: 'L' },
  { label: 'XL', value: 'XL' },
  { label: '2XL', value: '2XL' },
  { label: '3XL', value: '3XL' },
  { label: '4XL', value: '4XL' },
  { label: '5XL', value: '5XL' },
  { label: '6XL', value: '6XL' },
  { label: '7XL', value: '7XL' },
  { label: '8XL', value: '8XL' },
] as const;

export const STORE_CONFIRM_SIZES_DESIGN_HEADER_SUBTITLE =
  'All available sizes are selected. Uncheck to hide from buyers.';

export const STORE_CONFIRM_SIZES_TOOLTIP = {
  text: 'Our database says this product doesn’t come in this size. If you’re confident it does, you can add it manually.',
  width: '350px',
} as const;

export const STORE_CONFIRM_SIZES_NOTE = {
  PLACEHOLDER: 'Add a Note',
  REQUIRED_MSG: '* Required',
  MAX_FILE_SIZE_BYTES: 20_000_000,
} as const;

export const STORE_CONFIRM_SIZES_EXT_DEFAULT_SIZES = ['OS'] as const;

export const STORE_CONFIRM_SIZES_CUSTOM_SIZE_START_CODE = 99;

export const STORE_CONFIRM_SIZES_LOCKED_SIZE_TOOLTIP =
  "You can't remove this size because we got an order for this size.";

export const STORE_CONFIRM_SIZES_SOLD_OUT_RESTOCK_TOOLTIP_PREFIX = 'Restocking on';

export const STORE_CONFIRM_SIZES_SOLD_OUT_NO_RESTOCK_TOOLTIP = 'No restock date yet';

export const STORE_CONFIRM_SIZES_IN_STOCK_MSG_COLOR = '#8d90aa';

export const STORE_CONFIRM_SIZES_STOCK_INFO_UNAVAILABLE_MSG = 'Stock Info Unavailable';

export const ORDER_STOCK_FLAG_TYPES = ['low_stock', 'no_stock', 'low_and_no_stock'] as const;

export const FLAG_FILE_FOLDER = 'orders_needs_approval';
